/**
 * Costa Rica time. America/Costa_Rica is UTC-6 all year (no DST since 1992),
 * so conversions are fixed-offset arithmetic (plan §11.1, ADR-0004).
 *
 * Domain dates are ISO calendar strings "YYYY-MM-DD" (LocalDate); instants
 * are JS Dates in UTC.
 */
export type LocalDate = string & { readonly __brand?: "LocalDate" };

export const CR_OFFSET_MINUTES = -6 * 60;
export const CR_TIMEZONE = "America/Costa_Rica";

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseLocalDate(s: string): { y: number; m: number; d: number } {
  const match = ISO.exec(s);
  if (!match) throw new RangeError(`Not an ISO date: ${s}`);
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) {
    throw new RangeError(`Invalid calendar date: ${s}`);
  }
  return { y, m, d };
}

export function localDate(y: number, m: number, d: number): LocalDate {
  const probe = new Date(Date.UTC(y, m - 1, d));
  return fromUtcMidnight(probe);
}

function fromUtcMidnight(dt: Date): LocalDate {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${String(y).padStart(4, "0")}-${m}-${d}`;
}

function toUtcMidnight(s: LocalDate): Date {
  const { y, m, d } = parseLocalDate(s);
  return new Date(Date.UTC(y, m - 1, d));
}

/** The calendar date in Costa Rica at the given instant. */
export function crDateOf(instant: Date): LocalDate {
  return fromUtcMidnight(new Date(instant.getTime() + CR_OFFSET_MINUTES * 60_000));
}

/** The UTC instant of a Costa Rica wall-clock time on a date ("HH:MM", default 00:00). */
export function crInstant(date: LocalDate, time = "00:00"): Date {
  const [hh, mm] = time.split(":").map(Number);
  if (hh === undefined || mm === undefined || Number.isNaN(hh) || Number.isNaN(mm)) {
    throw new RangeError(`Bad time ${time}`);
  }
  const base = toUtcMidnight(date).getTime();
  return new Date(base + (hh * 60 + mm) * 60_000 - CR_OFFSET_MINUTES * 60_000);
}

/** Last instant of a Costa Rica calendar day, i.e. 23:59:59.999 CR. */
export function crEndOfDay(date: LocalDate): Date {
  return new Date(crInstant(addDays(date, 1)).getTime() - 1);
}

export function addDays(date: LocalDate, n: number): LocalDate {
  return fromUtcMidnight(new Date(toUtcMidnight(date).getTime() + n * 86_400_000));
}

/** Add calendar months, clamping the day to the target month's length. */
export function addMonths(date: LocalDate, n: number): LocalDate {
  const { y, m, d } = parseLocalDate(date);
  const idx = y * 12 + (m - 1) + n;
  const ty = Math.floor(idx / 12);
  const tm = (idx % 12) + 1;
  return localDate(ty, tm, Math.min(d, daysInMonth(ty, tm)));
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function lastDayOfMonth(y: number, m: number): LocalDate {
  return localDate(y, m, daysInMonth(y, m));
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date: LocalDate): number {
  return toUtcMidnight(date).getUTCDay();
}

export function isWeekend(date: LocalDate): boolean {
  const dow = dayOfWeek(date);
  return dow === 0 || dow === 6;
}

export function compareDates(a: LocalDate, b: LocalDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function daysBetween(from: LocalDate, to: LocalDate): number {
  return Math.round((toUtcMidnight(to).getTime() - toUtcMidnight(from).getTime()) / 86_400_000);
}

export function minDate(a: LocalDate, b: LocalDate): LocalDate {
  return a <= b ? a : b;
}
export function maxDate(a: LocalDate, b: LocalDate): LocalDate {
  return a >= b ? a : b;
}
